"use client";
import { useState } from "react";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import GlowingButton from "~/components/reusables/glowing-button";
import { fetchQRNGAPI } from "app/actions";
import Link from "next/link";
import { OnClickTrackers } from "~/components/reusables/on-click-trackers";

interface RandomNumberResponse {
  random_number: {
    binary: string;
    hex: string;
    decimal: string;
    octal: string;
  };
  num_of_available_bits: number;
}

export function RandomNumberForm() {
  const [numBits, setNumBits] = useState("100");
  const [formatType, setFormatType] = useState("hex");
  const [result, setResult] = useState<
    RandomNumberResponse["random_number"] | null
  >(null);
  const [loading, setLoading] = useState(false);
  const [showAllFormats, setShowAllFormats] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const res = await fetchQRNGAPI(
        `/get_random_number?num_bits=${numBits}&format_type=${formatType}&full_response=true`,
        "Couldn't fetch random number data",
      );

      if (res.status === 429) {
        setError("Too many requests. Please try again later.");
        return;
      }

      const data: RandomNumberResponse = await res.data;
      setResult(data.random_number);

      // TODO: Temerporarily using fetch on client side, should try to move it back to server side
      // const response = await fetch(
      //   `https://qrng-backend.dev.outshift.ai/get_random_number?num_bits=${numBits}&format_type=${formatType}&full_response=true`,
      // );

      // if (response.status === 429) {
      //   setError("Too many requests. Please try again later.");
      //   return;
      // }

      // if (!response.ok) {
      //   throw new Error("Failed to fetch random number");
      // }

      // const data: RandomNumberResponse = await response.json();
      // setResult(data.random_number);
    } catch {
      setError(
        "An error occurred while fetching the random number. Please try again.",
      );
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const getFormatLabel = (format: string) => {
    switch (format) {
      case "binary":
        return "Binary";
      case "hex":
        return "Hexadecimal";
      case "decimal":
        return "Decimal";
      case "octal":
        return "Octal";
      default:
        return format;
    }
  };

  return (
    <div className="flex w-full flex-col gap-4 rounded-lg bg-qrng-purple-900 bg-opacity-75 p-6 backdrop-blur-sm md:gap-6 md:p-8 lg:p-12">
      <h1 className="font-h4">
        Outshift Quantum Random Number Generator (QRNG)
      </h1>
      <div className="font-p">
        Quantum randomness is nature’s greatest wildcard, and we’ve harnessed it
        in ways that classical methods can’t match. True randomness isn’t just a
        scientific curiosity; it’s a critical ingredient for secure and reliable
        systems in today’s world. Experience quantum in action with our QRNG
        online generator. 
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div
            className="white relative rounded border border-red-500 bg-red-500/10 px-4 py-3"
            role="alert"
          >
            <span className="block sm:inline">{error}</span>
          </div>
        )}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-16 ">
          <div className="space-y-4">
            <label className="font-p-sm">Number of bits (up to 10,000)</label>
            <Input
              type="number"
              value={numBits}
              onChange={(e) => setNumBits(e.target.value)}
              min="100"
              max="10000"
              className="font-p border-qrng-purple-100 bg-qrng-purple-200 text-white"
            />
          </div>

          <div className="space-y-4">
            <label className="font-p-sm">Format Type</label>
            <Select value={formatType} onValueChange={setFormatType}>
              <SelectTrigger className="font-p border-qrng-purple-100 bg-qrng-purple-200 text-white">
                <SelectValue placeholder="Select format" />
              </SelectTrigger>
              <SelectContent className="bg-white text-black">
                <SelectItem value="hex">Hexadecimal</SelectItem>
                <SelectItem value="binary">Binary</SelectItem>
                <SelectItem value="decimal">Decimal</SelectItem>
                <SelectItem value="octal">Octal</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex py-6">
          <GlowingButton type="submit" disabled={loading} className="">
            <OnClickTrackers
              segmentMsg="generate random number clicked"
              segmentOpt={{
                link_label: "Generate random number",
                location: "body",
                location_detail: "center-body",
                resource_interaction: "generate random number",
              }}
            >
              <div className="font-btn-lg">
                {loading ? "Generating..." : "Generate random number"}
              </div>
            </OnClickTrackers>
          </GlowingButton>
        </div>

        {result ? (
          <div className="mt-4 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-h5 ">Results</h3>
              <Button
                type="button"
                variant={"outline"}
                onClick={() => setShowAllFormats(!showAllFormats)}
                className="bg-qrng-purple-200 text-white hover:bg-qrng-purple-900 hover:text-white"
              >
                {showAllFormats ? "Show Selected Only" : "Show All Formats"}
              </Button>
            </div>
            {showAllFormats ? (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <label className="font-p-sm">Binary</label>
                  <div className="flex gap-2">
                    <Input
                      value={result.binary}
                      readOnly
                      className="border-gray-600 bg-qrng-purple-200 font-mono text-white"
                    />
                    <Button
                      type="button"
                      onClick={() => copyToClipboard(result.binary)}
                      className="bg-blue-600 text-white hover:bg-blue-700"
                    >
                      Copy
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="font-p-sm">Hexadecimal</label>
                  <div className="flex gap-2">
                    <Input
                      value={result.hex}
                      readOnly
                      className="border-gray-600 bg-qrng-purple-200 font-mono text-white"
                    />
                    <Button
                      type="button"
                      onClick={() => copyToClipboard(result.hex)}
                      className="bg-blue-600 text-white hover:bg-blue-700"
                    >
                      Copy
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="font-p-sm">Decimal</label>
                  <div className="flex gap-2">
                    <Input
                      value={result.decimal}
                      readOnly
                      className="border-gray-600 bg-qrng-purple-200 font-mono text-white"
                    />
                    <Button
                      type="button"
                      onClick={() => copyToClipboard(result.decimal)}
                      className="bg-blue-600 text-white hover:bg-blue-700"
                    >
                      Copy
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="font-p-sm">Octal</label>
                  <div className="flex gap-2">
                    <Input
                      value={result.octal}
                      readOnly
                      className="border-gray-600 bg-qrng-purple-200 font-mono text-white"
                    />
                    <Button
                      type="button"
                      onClick={() => copyToClipboard(result.octal)}
                      className="bg-blue-600 text-white hover:bg-blue-700"
                    >
                      Copy
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <label className="font-p-sm">
                  {getFormatLabel(formatType)}
                </label>
                <div className="flex gap-2">
                  <Input
                    value={result[formatType as keyof typeof result]}
                    readOnly
                    className="border-gray-600 bg-qrng-purple-200 font-mono text-white"
                  />
                  <Button
                    type="button"
                    onClick={() =>
                      copyToClipboard(result[formatType as keyof typeof result])
                    }
                    className="bg-blue-600 text-white hover:bg-blue-700"
                  >
                    Copy
                  </Button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <>
            <div>
              <h6 className="font-h6 uppercase">Get in touch</h6>
              <div className="font-btn-lg pt-2 font-light">
                Interested in exploring how true quantum randomness can enhance
                your security and innovation?{" "}
                <Link
                  href="mailto:outshiftbycisco@cisco.com"
                  target="_blank"
                  className="underline hover:text-neutral-300"
                >
                  Contact us
                </Link>
                , we’d love to chat!
              </div>
            </div>
            <div className="font-p-sm pt-3 font-extralight italic">
              Outshift QRNG provided on this website is for demonstration
              purposes only and is offered "as-is" without any warranties.
              Please contact us if you're interested in using Outshift QRNG for
              development or high-security applications.
            </div>
          </>
        )}
      </form>
    </div>
  );
}
